import { Direction } from "@angular/cdk/bidi";
import { EventEmitter, TemplateRef, Type, ViewContainerRef } from "@angular/core";

import { UiButtonShape, UiButtonSize, UiButtonType } from "../button/models/button.models";
import { NzSafeAny } from "ng-zorro-antd/core/types";

export type UiOnClickCallback<T> = (instance: T) => (false | void | object) | Promise<false | void | object>;

export type UiModalModels = "default" | "confirm"; // Different modal styles we have supported

export type UiConfirmType = "confirm" | "info" | "success" | "error" | "warning"; // Subtypes of Confirm Modal

export interface UiStyleObjectLike {
  [key: string]: string;
}

const noopFun = (): void => void 0;

export class UiModalOptions<T = NzSafeAny, R = NzSafeAny> {
  public uiCentered?: boolean = false;
  public uiClosable?: boolean = true;
  public uiOkLoading?: boolean = false;
  public uiOkDisabled?: boolean = false;
  public uiCancelDisabled?: boolean = false;
  public uiCancelLoading?: boolean = false;
  public uiAutofocus?: "ok" | "cancel" | "auto" | null = "auto";
  public uiMask?: boolean;
  public uiMaskClosable?: boolean;
  public uiKeyboard?: boolean = true;
  public uiZIndex?: number = 1000;
  public uiWidth?: number | string = 520;
  public uiCloseIcon?: string | TemplateRef<void> = "close";
  public uiOkType?: UiButtonType = "primary";
  public uiOkDanger?: boolean = false;
  public uiType?: UiModalModels = "default";
  public uiOnCancel?: EventEmitter<T> | UiOnClickCallback<T> = noopFun;
  public uiOnOk?: EventEmitter<T> | UiOnClickCallback<T> = noopFun;
  public uiComponentParams?: Partial<T>;
  public uiMaskStyle?: UiStyleObjectLike;
  public uiBodyStyle?: UiStyleObjectLike;
  public uiWrapClassName?: string;
  public uiClassName?: string;
  public uiStyle?: object;
  public uiTitle?: string | TemplateRef<object>;
  public uiFooter?: string | TemplateRef<object> | Array<UiModalButtonOptions<T>> | null; // Default Modal ONLY
  public uiCancelText?: string | null;
  public uiOkText?: string | null;
  public uiContent?: string | TemplateRef<NzSafeAny> | Type<T>;
  public uiCloseOnNavigation?: boolean;
  public uiViewContainerRef?: ViewContainerRef;
  // Template use only
  public uiAfterOpen?: EventEmitter<void>;
  public uiAfterClose?: EventEmitter<R>;

  // Confirm
  public uiIconType?: string = "question-circle";
  public uiDirection?: Direction;
}

export interface UiModalButtonOptions<T = NzSafeAny> {
  label: string;
  type?: UiButtonType;
  danger?: boolean;
  shape?: UiButtonShape;
  ghost?: boolean;
  size?: UiButtonSize;
  autoLoading?: boolean; // Default: true, indicate whether show loading automatically while onClick returned a Promise

  // [NOTE] "componentInstance" will refer to the component's instance when using Component
  show?: boolean | ((this: UiModalButtonOptions<T>, contentComponentInstance?: T) => boolean);
  loading?: boolean | ((this: UiModalButtonOptions<T>, contentComponentInstance?: T) => boolean); // This prop CAN'T use with autoLoading=true
  disabled?: boolean | ((this: UiModalButtonOptions<T>, contentComponentInstance?: T) => boolean);
  onClick?(this: UiModalButtonOptions<T>, contentComponentInstance?: T): NzSafeAny | Promise<NzSafeAny>;
  [key: string]: NzSafeAny;
}
